import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
// import Popover from 'antd/es/popover';

import { IconBtnLoading, IconRcQRCodeRefreshIcon } from '@/assets';
import QRCode from '@/components/QRCode';
import { Popup } from '@/components';
import { IconLoginQrcode } from '@/assets/login';
import { useSwitch, useCountdown } from '@/hooks';

import { GenerateQRCode, useGenerateQRCode, useGetQRCodeStatus } from '../../hooks/qrcode';
import { APPLoginModal } from '../APPLoginModal';
import { APPLoginSuccessModal } from '../AppLoginSuccessModal';
import style from './index.module.scss';

export const AppLoginQrcode = () => {
  const { t } = useTranslation();

  const [showLogin, setShowLogin] = useState(false);
  const { on, turnOff, turnOn } = useSwitch();
  const {
    on: successLogin,
    turnOff: closeSucceededLoginModal,
    turnOn: openSuccessLoginModal,
  } = useSwitch();
  const [qrcodeData, setQRCodeData] = useState<GenerateQRCode>();

  const switchLogin = useCallback(
    (data: GenerateQRCode) => {
      turnOff();
      setQRCodeData(data);
      setShowLogin(true);
    },
    [turnOff],
  );

  const closeLogin = useCallback(() => {
    setQRCodeData(undefined);
    setShowLogin(false);
  }, []);

  return (
    <>
      <Popup
        on="hover"
        open={on}
        contentStyle={{
          padding: 0,
          borderRadius: 8,
        }}
        onClose={turnOff}
        onOpen={turnOn}
        position="bottom center"
        trigger={<img src={IconLoginQrcode} alt="" className={style.qrIcon} />}>
        <div className={style.qrcodeOverlay}>
          <div className={style.qrcodeTitle}>{t('APPScanQRCodeModal.152bou3')}</div>
          <RealQRCode switchLogin={switchLogin} />
        </div>
      </Popup>
      {showLogin && (
        <APPLoginModal
          onSuccess={openSuccessLoginModal}
          close={closeLogin}
          qrcodeData={qrcodeData}
        />
      )}
      {successLogin && <APPLoginSuccessModal close={closeSucceededLoginModal} />}
    </>
  );
};

type ScanQRCodeModalProps = {
  turnOff(): void;
  switchLogin(data: GenerateQRCode): void;
};

export function RealQRCode({ switchLogin }: { switchLogin: ScanQRCodeModalProps['switchLogin'] }) {
  const { t } = useTranslation();

  const [isActive, setIsActive] = useState(false);

  const { seconds, startCountdown } = useCountdown(90);

  const [startCount, setStartCount] = useState(false);

  const { data, refetch, isFetching } = useGenerateQRCode();
  const [qrcodeStatus, setQRCodeStatus] = useState<'generated' | 'scanned' | 'confirmed'>(
    'generated',
  );

  const { data: codeStatus } = useGetQRCodeStatus(data?.id || '', qrcodeStatus);
  const requestQRCode = useCallback(() => {
    refetch();
    startCountdown();
  }, [refetch, startCountdown]);

  const refreshQRCode = useCallback(() => {
    if (!isActive) {
      setIsActive(true);
      requestQRCode();
      const timer = setTimeout(() => {
        setIsActive(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isActive, requestQRCode]);

  useEffect(() => {
    if (seconds === 0) {
      requestQRCode();
    }
  }, [seconds, requestQRCode]);

  useEffect(() => {
    if (codeStatus?.state === 'scanned') {
      setQRCodeStatus(codeStatus?.state);
      codeStatus && switchLogin(codeStatus);
    }
  }, [codeStatus, codeStatus?.state, qrcodeStatus, requestQRCode, switchLogin]);

  useEffect(() => {
    if (!startCount && data?.id) {
      setStartCount(true);
      startCountdown();
    }
  }, [startCount, startCountdown, data?.id]);

  return (
    <>
      <div className={style.qrcodeContainer}>
        {isFetching ? (
          <IconBtnLoading />
        ) : (
          data?.id && <QRCode url={data?.id} size={180} level={'H'} />
        )}
      </div>

      {data?.id ? (
        <div className={style.refresh}>
          <IconRcQRCodeRefreshIcon
            onClick={refreshQRCode}
            className={cx(style.refreshIcon, isActive && style.refreshActive)}
          />
          <div>
            {t('APPScanQRCodeModal.rn7iju')}
            <span className={style.seconds}> {seconds} </span>
            {t('APPScanQRCodeModal.wky0e7')}
          </div>
        </div>
      ) : (
        <div className={style.refresh} />
      )}
    </>
  );
}
